import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { concat, find, findIndex, forEach, get, isEmpty, isString, map } from 'lodash';

import ChartComponent from '../ChartComponent';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

const CARD_MARGIN = 15;
const TEXT_OFFSET = 5;
const TEXT_OPACITY = 0.6;
const BLACK_STROKE = '#000';
const WHITE_STROKE = '#fff';
const AXIS_OPACITY = 0.2;
const AXIS_WIDTH_5 = 5;
const AXIS_WIDTH_1 = 1;
const AXIS_TICK_NUMBER = 5;
const AXIS_DISPLACEMENT = '0.35em';
const TEXT_INICIAL_SIZE = 10;
const CHART_CLASS = 'radial-ordered-stacked-bar-chart';

class RadialOrderedStackedBarChart extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  drawChart = () => {
    const {
      alpha,
      borderAlpha,
      borderColor,
      colorRange,
      dataNamed,
      dataNumeric,
      globalProperties,
      height,
      isCard,
      numericName,
      selectedElements,
      weight,
      width
    } = this.props;

    const textSize = get(find(globalProperties, ['name', 'textSize']), 'value', TEXT_INICIAL_SIZE);

    const svg = d3
      .select(`#svg-${this.id}`)
      .attr('viewBox', [0, 0, width, height])
      .style('overflow', 'visible');

    const numericNamesAsObjects = map(numericName, name => ({ name }));

    const data = map(dataNamed, (namedValue, namedIndex) => {
      const objectToReturn = {
        name: namedValue
      };
      let total = 0;
      forEach(dataNumeric, (dataNumericSubarray, numericSubarrayIndex) => {
        total += Number(dataNumericSubarray[namedIndex]);
        objectToReturn[numericName[numericSubarrayIndex]] = dataNumericSubarray[namedIndex];
      });
      objectToReturn.total = total;
      return objectToReturn;
    }).sort((a, b) => b.total - a.total);

    const series = d3
      .stack()
      .keys(numericName)(data)
      // eslint-disable-next-line no-return-assign, no-sequences, no-param-reassign
      .map(d => (d.forEach(v => (v.key = d.key)), d));

    const radiusToUse = Math.min(width, height);

    const outerRadius = radiusToUse / 2 - CARD_MARGIN;

    const innerRadius = radiusToUse / 5;

    const x = d3
      .scaleBand()
      .domain(map(data, d => d.name))
      .range([0, 2 * Math.PI])
      .padding(0);

    const y = d3
      .scaleLinear()
      .domain([
        Math.min(
          0,
          d3.min(series, d => d3.min(d, d2 => d2[1]))
        ),
        d3.max(series, d => d3.max(d, d2 => d2[1]))
      ])
      .nice()
      .range([innerRadius, outerRadius]);

    const pie = d3.pie().value(() => x.bandwidth());

    const arcs = pie(data);

    const yAxis = g => {
      if (selectedElements.Text) {
        g.selectAll('g')
          .data(arcs)
          .join('g')
          .append('g')
          .attr(
            'text-anchor',
            d => `${(d.startAngle + d.endAngle) / 2 < Math.PI ? 'end' : 'start'}`
          )
          .attr(
            'transform',
            d =>
              `rotate(${
                (d.startAngle + d.endAngle) / 2 < Math.PI
                  ? ((d.startAngle / 2 + d.endAngle / 2) * 180) / Math.PI - 90
                  : ((d.startAngle / 2 + d.endAngle / 2 + Math.PI) * 180) / Math.PI - 90
              }) translate(${
                (d.startAngle + d.endAngle) / 2 < Math.PI
                  ? innerRadius - TEXT_OFFSET
                  : TEXT_OFFSET - innerRadius
              }, 4)`
          )
          .append('text')
          .text(d => d.data.name)
          .style('font-size', textSize)
          .attr('opacity', TEXT_OPACITY);
      }
    };

    const xAxis = g => {
      if (selectedElements.Axis) {
        g.attr('text-anchor', 'middle')
          .attr('font-family', 'sans-serif')
          .attr('font-size', textSize)
          .selectAll('g')
          .data(y.ticks(5).reverse())
          .join('g')
          .attr('fill', 'none')
          .append('circle')
          .attr('stroke', BLACK_STROKE)
          .attr('stroke-opacity', AXIS_OPACITY)
          .attr('r', y);
        if (selectedElements.Text) {
          g.selectAll('g')
            .data(concat(y.ticks(AXIS_TICK_NUMBER).reverse(), y.ticks(AXIS_TICK_NUMBER).reverse()))
            .join('g')
            .append('text')
            .attr('y', (d, i, array) => {
              if (i / array.length >= 0.5) return y(d);
              return -y(d);
            })
            .attr('dy', AXIS_DISPLACEMENT)
            .attr('stroke', WHITE_STROKE)
            .attr('stroke-width', AXIS_WIDTH_5)
            .text(d => `${d.toFixed(0)}`);
          g.selectAll('g')
            .data(concat(y.ticks(AXIS_TICK_NUMBER).reverse(), y.ticks(AXIS_TICK_NUMBER).reverse()))
            .join('g')
            .append('text')
            .attr('y', (d, i, array) => {
              if (i / array.length >= 0.5) return y(d);
              return -y(d);
            })
            .attr('dy', AXIS_DISPLACEMENT)
            .attr('stroke', BLACK_STROKE)
            .attr('stroke-width', AXIS_WIDTH_1)
            .text(d => `${d.toFixed(0)}`);
        }
      }
    };

    const arc = d3
      .arc()
      .innerRadius(d => y(d[0]))
      .outerRadius(d => y(d[1]))
      .startAngle(d => x(d.data.name))
      .endAngle(d => x(d.data.name) + x.bandwidth())
      .padAngle(0.02)
      .padRadius(innerRadius);

    const bars = svg
      .append('g')
      .attr('class', `${this.id}`)
      .selectAll('g')
      .data(series)
      .join('g')
      .selectAll('path')
      .data(d => d)
      .join('path')
      .attr('class', `${this.id}`)
      .attr('fill', (d, i) => {
        if (isString(colorRange)) {
          return colorRange;
        }
        if (isString(colorRange[findIndex(numericNamesAsObjects, ['name', d.key])])) {
          return colorRange[findIndex(numericNamesAsObjects, ['name', d.key])];
        }
        return colorRange[findIndex(numericNamesAsObjects, ['name', d.key])][i];
      })
      .attr('fill-opacity', d => alpha[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke', d => borderColor[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke-opacity', d => borderAlpha[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke-width', d => weight[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('d', arc)
      .attr('transform', `translate(${width / 2},${height / 2})`);
    if (!isCard) {
      bars.append('title').text(d => `${d.data.name} ${d.key}: ${d.data[d.key]}`);

      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('transform', `translate(${width / 2},${height / 2})`)
        .call(yAxis);

      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('transform', `translate(${width / 2},${height / 2})`)
        .call(xAxis);

      svg
        .selectAll('text')
        .attr('font-size', get(find(globalProperties, ['name', 'textSize']), 'value', 10));
    }
  };
}

RadialOrderedStackedBarChart.defaultProps = {
  width: 0,
  height: 0,
  dataNamed: ['A', 'B', 'C'],
  dataNumeric: [
    [1, 1.5, 2],
    [1.1, 1.5, 2],
    [1.2, 1.5, 2]
  ],
  colorRange: ['rgba(242,206,75)', 'rgb(40,38,35)', 'rgb(137,165,80)'],
  alpha: [0.9, 0.9, 0.9],
  weight: [1, 1, 1],
  numericName: ['AtributoA', 'AtributoB', 'AtributoC'],
  borderColor: ['black', 'black', 'black'],
  borderAlpha: [0.9, 0.9, 0.9],
  globalProperties: [],
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  isCard: false
};
RadialOrderedStackedBarChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNumeric: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
  ]),
  dataNamed: PropTypes.array,
  colorRange: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]))
  ]),
  alpha: PropTypes.arrayOf(PropTypes.number),
  weight: PropTypes.arrayOf(PropTypes.number),
  borderColor: PropTypes.arrayOf(PropTypes.string),
  borderAlpha: PropTypes.arrayOf(PropTypes.number),
  globalProperties: PropTypes.array,
  selectedElements: PropTypes.object,
  numericName: PropTypes.arrayOf(PropTypes.string),
  isCard: PropTypes.bool
};

export default RadialOrderedStackedBarChart;
